<template>
  <div class="login-container">
    <!-- Left Panel: Brand -->
    <div class="login-left-panel">
      <div class="login-orb login-orb-1"></div>
      <div class="login-orb login-orb-2"></div>

      <div class="login-brand">
        <div class="login-brand-logo animate-fade-in-down">
          <div class="login-brand-icon">
            <img src="/logo.png" alt="" width="44" height="44">
          </div>
          <span class="login-brand-name">Intelvo Tech</span>
        </div>

        <div class="animate-fade-in-up">
          <h1 class="login-hero-title">Task Management<br>Reimagined.</h1>
          <p class="login-hero-desc">
            Organize complex projects with hierarchical task trees, role-based execution, and real-time collaboration — built for teams that ship fast.
          </p>
        </div>

        <div class="login-features animate-fade-in-up">
          <div class="login-feature-item">
            <div class="login-feature-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            </div>
            <span class="login-feature-text">Hierarchical Task Trees</span>
          </div>
          <div class="login-feature-item">
            <div class="login-feature-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <span class="login-feature-text">Role-Based Access Control</span>
          </div>
          <div class="login-feature-item">
            <div class="login-feature-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </div>
            <span class="login-feature-text">Automated Audit Trails</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Panel: Login Form -->
    <div class="login-right-panel">
      <div class="login-form-wrapper animate-fade-in">
        <!-- Mobile only: the brand panel is hidden below 1024px. -->
        <div class="login-mobile-brand">
          <img src="/logo.png" alt="" width="96" height="96">
        </div>

        <div class="login-form-header">
          <h2 class="login-title">Welcome Back</h2>
          <p class="login-subtitle">Sign in to your workspace</p>
        </div>

        <div v-if="errorMessage" class="alert alert-error" role="alert">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          {{ errorMessage }}
        </div>

        <form class="login-form" novalidate @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="login-email" class="form-label">Email</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              <input id="login-email" autocomplete="email"
                v-model="email"
                type="email"
                class="form-input"
                placeholder="you@intelvo.com"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="login-password" class="form-label">Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <input id="login-password" autocomplete="current-password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="Enter your password"
                required
              />
              <button type="button" class="toggle-password" :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
                <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </div>
            <div class="forgot-password-container">
              <router-link to="/forgot-password" class="forgot-link">Forgot password?</router-link>
            </div>
          </div>

          <button type="submit" class="btn-primary" :disabled="isLoading">
            <span v-if="!isLoading">Sign In</span>
            <span v-else>Signing in...</span>
          </button>
        </form>

        <div class="auth-footer">
          <span>Don't have an account? <router-link to="/signup" class="signup-link-style">Sign Up</router-link></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../stores/authStore';

export default {
  name: 'LoginView',
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      isLoading: false,
      errorMessage: ''
    };
  },
  mounted() {
    document.documentElement.classList.add('auth-page');
  },
  beforeUnmount() {
    document.documentElement.classList.remove('auth-page');
  },
  methods: {
    async handleLogin() {
      // Checked here rather than by the browser's own popups, so the message
      // reads like the rest of the app and sits where the error box is.
      this.errorMessage = '';
      if (!this.email.trim()) { this.errorMessage = 'Enter your email.'; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())) {
        this.errorMessage = 'Enter a valid email, like name@company.com.';
        return;
      }
      if (!this.password) { this.errorMessage = 'Enter your password.'; return; }
      this.isLoading = true;
      try {
        const params = new URLSearchParams();
        params.append('username', this.email);
        params.append('password', this.password);

        const res = await this.$api2.post('/token', params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });

        const authStore = useAuthStore();
        // Permissions aren't in the login response — MainLayout's
        // fetchCurrentUser() fills them in from /profile on the next screen.
        authStore.setAuthData(res.data.access_token, res.data.user);
        this.$router.replace('/');
      } catch (err) {
        const msg = err.response?.data?.message || 'Invalid email or password.';
        this.errorMessage = msg;
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>
